import axios from 'axios';

// API base URL - in Docker, the gateway handles everything
const API_BASE = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (username, password) => api.post('/api/auth/login', { username, password }),
  listCompanies: () => api.get('/api/auth/companies'),
  createCompany: (data) => api.post('/api/auth/companies', data),
  me: () => api.get('/api/auth/me'),
  register: (data) => api.post('/api/auth/register', data),
  listUsers: () => api.get('/api/auth/users'),
  updateUser: (id, data) => api.put(`/api/auth/users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/auth/users/${id}`),
  getAuditLogs: (limit = 100) => api.get(`/api/auth/audit?limit=${limit}`),
  changePassword: (currentPassword, newPassword) => api.post('/api/auth/change-password', { currentPassword, newPassword }),
};

// Swarm / Docker - Services are the primary management entity
// All endpoints go to the gateway's built-in SwarmController at /api/swarm/
export const swarmApi = {
  // System info
  getInfo: () => api.get('/api/swarm/info'),
  getObservabilityStatus: () => api.get('/api/swarm/observability-status'),

  // Services (primary)
  listServices: () => api.get('/api/swarm/services'),
  inspectService: (id) => api.get(`/api/swarm/services/${id}`),
  getServiceLogs: (id, tail = 200) => api.get(`/api/swarm/services/${id}/logs?tail=${tail}`),
  getServiceContainers: (id) => api.get(`/api/swarm/services/${id}/containers`),
  scaleService: (id, replicas) => api.post(`/api/swarm/services/${id}/scale?replicas=${replicas}`),
  restartService: (id) => api.post(`/api/swarm/services/${id}/restart`),
  rollbackService: (id) => api.post(`/api/swarm/services/${id}/rollback`),
  removeService: (id) => api.delete(`/api/swarm/services/${id}`),
  updateService: (id, data) => api.post(`/api/swarm/services/${id}/update`, data),

  // Volumes (data persistence)
  listVolumes: () => api.get('/api/swarm/volumes'),
  inspectVolume: (name) => api.get(`/api/swarm/volumes/${name}`),
  createVolume: (name, driver = 'local', labels = {}) => api.post('/api/swarm/volumes', { name, driver, labels }),
  removeVolume: (name, force = false) => api.delete(`/api/swarm/volumes/${name}?force=${force}`),

  // Containers (secondary - for debugging/drill-down)
  listContainers: (all = true) => api.get(`/api/swarm/containers?all=${all}`),
  inspectContainer: (id) => api.get(`/api/swarm/containers/${id}`),
  getContainerLogs: (id, tail = 100) => api.get(`/api/swarm/containers/${id}/logs?tail=${tail}`),
  startContainer: (id) => api.post(`/api/swarm/containers/${id}/start`),
  stopContainer: (id) => api.post(`/api/swarm/containers/${id}/stop`),
  removeContainer: (id) => api.delete(`/api/swarm/containers/${id}`),

  // Swarm nodes & tasks
  listNodes: () => api.get('/api/swarm/nodes'),
  listTasks: () => api.get('/api/swarm/tasks'),
  pruneContainers: () => api.post('/api/swarm/containers/prune'),
  pruneImages: () => api.post('/api/swarm/images/prune'),
  pruneVolumes: () => api.post('/api/swarm/volumes/prune'),
  getDiskUsage: () => api.get('/api/swarm/disk-usage'),

  // AI Governance
  listAiRecommendations: (status) => api.get(`/api/ai/recommendations${status ? `?status=${status}` : ''}`),
  getAiRecommendation: (id) => api.get(`/api/ai/recommendations/${id}`),
  reviewAiRecommendation: (id, decision, comment) => api.post(`/api/ai/recommendations/${id}/review`, { decision, comment }),
  listAiDecisions: (limit = 50) => api.get(`/api/ai/decisions?limit=${limit}`),
  getAiSettings: () => api.get('/api/ai/settings'),
  getAiStats: () => api.get('/api/ai/stats'),
  updateAiSettings: (data) => api.put('/api/ai/settings', data),
  triggerAiEvaluation: (serviceId) => api.post(`/api/ai/evaluate/${serviceId}`),
  explainService: (serviceIdOrName) => api.post(`/api/ai/explain/${encodeURIComponent(serviceIdOrName)}`),
  getMetricsStatus: () => api.get('/api/ai/metrics/status'),

  // Intent Console
  listIntents: (limit = 50, status) =>
    api.get(`/api/ai/intents?limit=${limit}${status ? `&status=${status}` : ''}`),
  getIntent: (id) => api.get(`/api/ai/intents/${id}`),
  createIntent: (message, context, options = {}) =>
    api.post('/api/ai/intents', {
      message,
      context,
      autoExecuteIfAllowed: options.autoExecuteIfAllowed !== false,
      dryRun: !!options.dryRun,
      environment: options.environment,
    }),
  reviewIntent: (id, decision, comment) =>
    api.post(`/api/ai/intents/${id}/review`, { decision, comment }),
  cancelIntent: (id) => api.post(`/api/ai/intents/${id}/cancel`),
  getIntentPendingCount: () => api.get('/api/ai/intents/pending-count'),

  // Workloads (desired-state deploy)
  listWorkloads: () => api.get('/api/workloads'),
  getWorkload: (id) => api.get(`/api/workloads/${id}`),
  createWorkload: (data) => api.post('/api/workloads', data),
  updateWorkload: (id, data) => api.put(`/api/workloads/${id}`, data),
  deleteWorkload: (id, removeFromSwarm = true) =>
    api.delete(`/api/workloads/${id}?removeFromSwarm=${removeFromSwarm}`),
  deployWorkload: (id) => api.post(`/api/workloads/${id}/deploy`),

  /**
   * Open an EventSource for live cluster events.
   * EventSource cannot set Authorization headers, so JWT is passed as query param.
   */
  // Policy & secrets (admin)
  listPolicyRules: () => api.get('/api/policy/rules'),
  createPolicyRule: (data) => api.post('/api/policy/rules', data),
  updatePolicyRule: (id, data) => api.put(`/api/policy/rules/${id}`, data),
  deletePolicyRule: (id) => api.delete(`/api/policy/rules/${id}`),
  evaluatePolicy: (data) => api.post('/api/policy/evaluate', data),
  getPolicyEnvironment: () => api.get('/api/policy/environment'),
  listSecrets: () => api.get('/api/secrets'),
  createSecret: (data) => api.post('/api/secrets', data),
  deleteSecret: (id) => api.delete(`/api/secrets/${id}`),

  openEventStream: (onEvent, onError) => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const base = process.env.REACT_APP_API_URL || '';
    const es = new EventSource(`${base}/api/events/stream?access_token=${encodeURIComponent(token)}`);
    const handler = (e) => {
      try {
        const data = JSON.parse(e.data);
        onEvent?.(e.type || data.type || 'message', data);
      } catch {
        onEvent?.(e.type || 'message', e.data);
      }
    };
    // Named events from server
    ['connected', 'service_state', 'ai_recommendation', 'ai_decision', 'intent', 'autoscale', 'message'].forEach((name) => {
      es.addEventListener(name, handler);
    });
    es.onmessage = handler;
    es.onerror = (err) => {
      onError?.(err);
    };
    return es;
  },
};

// Certificates
export const certsApi = {
  list: () => api.get('/api/certs'),
  status: (domain) => api.get(`/api/certs/${domain}/status`),
  renew: (domain, options = {}) => {
    const params = new URLSearchParams();
    if (domain) params.set('domain', domain);
    if (options.force) params.set('force', 'true');
    const query = params.toString();
    return api.post(`/api/certs/renew${query ? `?${query}` : ''}`);
  },
  issue: (domain, email) => api.post('/api/certs/issue', { domain, email }),
};

// Tests
export const testsApi = {
  list: (type, status, limit = 100) => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (status) params.set('status', status);
    params.set('limit', limit);
    return api.get(`/api/tests?${params}`);
  },
  summary: () => api.get('/api/tests/summary'),
  get: (id) => api.get(`/api/tests/${id}`),
  run: (type) => api.post(`/api/tests/run${type ? `?type=${type}` : ''}`),
  trends: (days = 30) => api.get(`/api/tests/trends?days=${days}`),
  clearResults: (status) => api.delete(`/api/tests/results${status ? `?status=${status}` : ''}`),
};

// Notifications
export const notificationsApi = {
  getSettings: () => api.get('/api/notifications/settings'),
  updateSettings: (data) => api.put('/api/notifications/settings', data),
  sendTest: () => api.post('/api/notifications/test'),
};

// Domain Routing
export const domainsApi = {
  list: () => api.get('/api/domains'),
  get: (id) => api.get(`/api/domains/${id}`),
  create: (data) => api.post('/api/domains', data),
  update: (id, data) => api.put(`/api/domains/${id}`, data),
  delete: (id) => api.delete(`/api/domains/${id}`),
  toggle: (id) => api.post(`/api/domains/${id}/toggle`),
  check: (id) => api.get(`/api/domains/${id}/check`),
};

// Networks
export const networksApi = {
  list: () => api.get(`/api/swarm/networks?_t=${Date.now()}`),
  inspect: (id) => api.get(`/api/swarm/networks/${id}`),
};

export default api;
